import { datadogLogs, type LogsEvent } from '@datadog/browser-logs';
import cloneDeepWith from 'lodash/cloneDeepWith';
import { matchPath } from 'react-router';
import { dunsRoutePattern } from '../../features/app/components/routing/Routes';
import { config } from '../config';
import type { RIOIdToken } from '../tokenHandling/IdToken';
import { trace } from './trace';

const clientToken: string | undefined = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
const isDatadogEnabled = Boolean(clientToken);

const sensitiveQueryParameters = ['access_token', 'code', 'id_token', 'session_state', 'state'];
const sensitiveQueryParameterRegex = new RegExp(
    `([?&])(${sensitiveQueryParameters.join('|')}|X-Amz-[^=&#\\s]+|AWSAccessKeyId|Signature)=([^&#\\s]*)`,
    'giu',
);

const redactSensitiveQueryParameters = (value: unknown) =>
    typeof value === 'string' ? value.replace(sensitiveQueryParameterRegex, '$1$2=REDACTED') : undefined;

const validDunsOrUnknown = (value: unknown): string =>
    typeof value === 'string' && /^[0-9]{9}$/u.test(value) ? value : 'unknown';

const selectedDunsFromLog = (log: LogsEvent): string => {
    try {
        // The view URL is captured with the log, so route changes cannot leave a stale selected DUNS.
        const pathname = log.view?.url ? new URL(log.view.url).pathname : '';
        return validDunsOrUnknown(matchPath(dunsRoutePattern, pathname)?.params.dunsNumber);
    } catch {
        return 'unknown';
    }
};

// The SDK sends the object passed to beforeSend, so changes must be applied in place.
const prepareLogForSending = (log: LogsEvent) => {
    Object.assign(log, cloneDeepWith(log, redactSensitiveQueryParameters));
    try {
        log.duns_number = selectedDunsFromLog(log);
        log.usr ??= {};
        log.usr.client_id = config.login.clientId || 'unknown';
    } catch {
        // Enriching a log must not interfere with application behavior.
    }
    return true;
};

const errorToMessage = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }
    if (typeof error !== 'object' || error === null) {
        return `Unexpected error: ${String(error)}`;
    }

    const { message, status } = error as { message?: unknown; status?: unknown };
    if (typeof message === 'string' && message) {
        return message;
    }
    if (status !== undefined) {
        return `Request failed with status ${String(status)}`;
    }
    return 'Unexpected error';
};

export const reportErrorToDatadog = (error: unknown, context?: Record<string, unknown>) => {
    if (isDatadogEnabled) {
        const errorDetails = typeof error === 'string' ? undefined : error;
        // The SDK also serialises non-Error values like rejected RTK query results, despite its Error-only type
        datadogLogs.logger.error(errorToMessage(error), context, errorDetails as Error | undefined);
        return;
    }

    trace('error', { error, ...context });
};

// Safari hides the URLs of extension scripts behind webkit-masked-url://
const browserExtensionUrlRegex = /(?:chrome-extension|moz-extension|safari-web-extension|webkit-masked-url):\/\//u;

// Browser extensions can run their scripts inside our page, so their errors reach our listeners as well
const isThrownByBrowserExtension = (error: unknown) => {
    const stack = (error as { stack?: unknown } | null | undefined)?.stack;
    return typeof stack === 'string' && browserExtensionUrlRegex.test(stack) && !stack.includes(window.location.origin);
};

// Keep a representative event per minute for repeated generic browser network failures.
// Contextual API reports are handled separately and are not rate limited here.
const lastGenericNetworkError = new Map<string, number>();
const reportUncaughtError = (error: unknown, errorOrigin: string) => {
    try {
        if (isThrownByBrowserExtension(error)) {
            return;
        }
        const message = error instanceof Error ? error.message : undefined;
        const genericNetworkError =
            message &&
            ['Failed to fetch', 'NetworkError when attempting to fetch resource.', 'Load failed'].includes(message)
                ? message
                : undefined;
        const now = Date.now();
        const key = genericNetworkError
            ? `${errorOrigin}:${genericNetworkError}:${error instanceof Error ? (error.stack ?? '') : ''}`
            : undefined;
        const lastReported = key ? lastGenericNetworkError.get(key) : undefined;
        if (lastReported !== undefined && now - lastReported < 60_000) {
            return;
        }
        reportErrorToDatadog(error, { errorOrigin });
        if (key) {
            if (lastGenericNetworkError.size >= 100) {
                lastGenericNetworkError.clear();
            }
            lastGenericNetworkError.set(key, now);
        }
    } catch {
        // Reporting an unrelated error must never create another application error.
    }
};

const reportUncaughtErrors = () => {
    window.addEventListener('error', (event) => {
        // Errors thrown by cross-origin scripts carry no error object, only the useless message "Script error."
        if (event.error) {
            reportUncaughtError(event.error, 'uncaught error');
        }
    });
    window.addEventListener('unhandledrejection', (event) => {
        reportUncaughtError(event.reason, 'unhandled promise rejection');
    });
};

export const configureDatadogLogging = () => {
    if (!isDatadogEnabled) {
        return;
    }

    datadogLogs.init({
        clientToken: clientToken as string,
        // Uncaught errors are reported by our own listeners, without the SDK's noisy network error forwarding.
        // Console errors are not forwarded: React logs every error caught by the ErrorBoundary there as well,
        // and oidc-client-ts logs expected events like an expired session as errors.
        forwardErrorsToLogs: false,
        // Logs carry the logged-in user, so an anonymous id kept in a cookie for a year adds nothing
        trackAnonymousUser: false,
        site: import.meta.env.VITE_DATADOG_SITE_URL,
        sessionSampleRate: 100,
        version: APP_VERSION,
        service: import.meta.env.VITE_DATADOG_SERVICE_NAME,
        env: import.meta.env.PROD ? config.environmentFromUri : 'local',
        beforeSend: prepareLogForSending,
    });
    datadogLogs.setGlobalContextProperty('account_id', import.meta.env.VITE_AWS_ACCOUNT_ID);
    datadogLogs.setGlobalContextProperty('team', import.meta.env.VITE_TEAM_IDENTIFIER);
    datadogLogs.setGlobalContextProperty('team_identifier', import.meta.env.VITE_TEAM_IDENTIFIER);

    reportUncaughtErrors();
};

type UserProfile = { sub: string; account?: string; logisticsTenant?: string };

export const setDatadogUser = (profile: UserProfile, token?: RIOIdToken) => {
    try {
        const roles = Array.isArray(token?.roles)
            ? token.roles.map((role) => role?.name).filter((name): name is string => typeof name === 'string' && !!name)
            : [];
        datadogLogs.setGlobalContextProperty(
            'logistics_tenant',
            profile.logisticsTenant || token?.logistics_tenant || 'unknown',
        );
        datadogLogs.setGlobalContextProperty('actor_duns_number', validDunsOrUnknown(token?.duns_number));
        datadogLogs.setUser({
            id: profile.sub || 'unknown',
            account: profile.account || 'unknown',
            role: roles.length ? roles : ['unknown'],
        });
    } catch {
        // A logging integration failure must never interrupt session renewal.
    }
};
